import { 
  coursesCol, 
  courseRef, 
  enrollmentsCol, 
  enrollmentRef,
  clientDoc,
  clientsCol
} from '@/backend/firebase/services/helpers/firestore-refs';
import { getDocs, query, orderBy, addDoc, updateDoc, where, serverTimestamp, getDoc, limit, increment } from 'firebase/firestore';
import type { 
  Course, 
  CourseWithId, 
  Enrollment, 
  EnrollmentWithId,
} from '@/shared/schema';

export const AcademyService = {
  // ==========================================
  // COURSES (The Product)
  // ==========================================

  getCourses: async (ownerId: string): Promise<CourseWithId[]> => {
    try {
      const q = query(
        coursesCol(ownerId),
        orderBy('audit.updatedAt', 'desc')
      );
      const snapshot = await getDocs(q);
      const courses = snapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id
      })) as CourseWithId[];
      
      return courses.filter(c => !c.audit?.deletedAt);
    } catch (error) {
      console.error(`[AcademyService:getCourses] ❌ Error fetching courses:`, error);
      return [];
    }
  },

  getCourseById: async (ownerId: string, courseId: string): Promise<CourseWithId | null> => {
    const docRef = courseRef(ownerId, courseId);
    const snap = await getDoc(docRef);
    if (!snap.exists()) return null;

    const data = snap.data();
    if (data.audit?.deletedAt) return null;

    return { ...data, id: snap.id } as CourseWithId;
  },

  createCourse: async (ownerId: string, data: Course): Promise<string> => {
    const docRef = await addDoc(coursesCol(ownerId), {
      ...data,
      ownerId,
      audit: {
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        deletedAt: null,
        createdBy: ownerId,
      }
    } as any);
    return docRef.id;
  },

  updateCourse: async (ownerId: string, courseId: string, data: Partial<Course>): Promise<void> => {
    const docRef = courseRef(ownerId, courseId);
    await updateDoc(docRef, {
      ...data,
      'audit.updatedAt': serverTimestamp(),
      'audit.updatedBy': ownerId,
    });
  },

  deleteCourse: async (ownerId: string, courseId: string): Promise<void> => {
    const docRef = courseRef(ownerId, courseId);
    await updateDoc(docRef, {
      'audit.deletedAt': serverTimestamp(),
      'audit.updatedAt': serverTimestamp(),
      'audit.updatedBy': ownerId,
    });
  },

  // ==========================================
  // ENROLLMENTS (The Access)
  // ==========================================

  createEnrollment: async (ownerId: string, data: Omit<Enrollment, 'audit'>): Promise<string> => {
    const docRef = await addDoc(enrollmentsCol(ownerId), {
      ...data,
      audit: {
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: ownerId,
      }
    } as any);

    // If it's a manual active enrollment, increment course count
    if (data.status === 'active' && data.courseId) {
        await updateDoc(courseRef(ownerId, data.courseId), {
            enrollmentCount: increment(1)
        }).catch(() => null);
    }

    return docRef.id;
  },

  getEnrollmentsByStudent: async (ownerId: string, studentUid: string): Promise<EnrollmentWithId[]> => {
    const q = query(
      enrollmentsCol(ownerId),
      where('studentUid', '==', studentUid),
      where('status', '==', 'active')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id })) as EnrollmentWithId[];
  },

  getEnrollmentsByCourse: async (ownerId: string, courseId: string): Promise<EnrollmentWithId[]> => {
    const q = query(
      enrollmentsCol(ownerId),
      where('courseId', '==', courseId),
      orderBy('audit.createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id })) as EnrollmentWithId[];
  },

  getAllEnrollments: async (ownerId: string): Promise<EnrollmentWithId[]> => {
    const q = query(
      enrollmentsCol(ownerId),
      orderBy('audit.createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id })) as EnrollmentWithId[];
  },

  getEnrollmentById: async (ownerId: string, enrollmentId: string): Promise<EnrollmentWithId | null> => {
    const docRef = enrollmentRef(ownerId, enrollmentId);
    const snapshot = await getDoc(docRef);
    if (!snapshot.exists()) return null;
    return { ...snapshot.data(), id: snapshot.id } as EnrollmentWithId;
  },

  getEnrollmentByToken: async (ownerId: string, token: string): Promise<EnrollmentWithId | null> => {
    const q = query(
      enrollmentsCol(ownerId),
      where('activationToken', '==', token),
      where('status', '==', 'pending'),
      limit(1)
    );
    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;
    
    const doc = snapshot.docs[0];
    return { ...doc.data(), id: doc.id } as EnrollmentWithId;
  },

  activateEnrollment: async (ownerId: string, enrollmentId: string, studentUid: string): Promise<void> => {
    const docRef = enrollmentRef(ownerId, enrollmentId);
    
    const snap = await getDoc(docRef);
    if (snap.exists() && snap.data().courseId) {
        await updateDoc(courseRef(ownerId, snap.data().courseId), {
            enrollmentCount: increment(1)
        }).catch(() => null);
    }

    await updateDoc(docRef, {
      studentUid,
      status: 'active',
      activationToken: null, 
      'audit.updatedAt': serverTimestamp(),
    });
  },

  revokeEnrollment: async (ownerId: string, enrollmentId: string): Promise<void> => {
    const docRef = enrollmentRef(ownerId, enrollmentId);
    const snap = await getDoc(docRef);

    if (snap.exists() && snap.data().status === 'active' && snap.data().courseId) {
        await updateDoc(courseRef(ownerId, snap.data().courseId), {
            enrollmentCount: increment(-1)
        }).catch(() => null);
    }

    await updateDoc(docRef, {
      status: 'revoked',
      'audit.updatedAt': serverTimestamp(),
    });
  },

  // ==========================================
  // MASTER STUDENTS (The Community)
  // Now unified under clients/{uid}.academy
  // ==========================================

  getStudents: async (ownerId: string): Promise<any[]> => {
    try {
      const q = query(
        clientsCol(ownerId),
        where('academy.isStudent', '==', true),
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => {
        const data = doc.data() as any;
        return {
          id: doc.id,
          ownerId,
          studentUid: data.studentUid || null,
          primaryClientId: doc.id,
          clientIds: [],
          profile: {
            name: data.name || '',
            phone: data.phone || '',
            email: data.email || '',
            birthdate: data.birthdate || '',
          },
          authorizedCourses: data.academy?.authorizedCourses || [],
          progress: data.academy?.progress || {},
          audit: data.audit,
        };
      });
    } catch (error) {
      console.error(`[AcademyService:getStudents] ❌ Error:`, error);
      return [];
    }
  },

  getStudentByUid: async (ownerId: string, studentUid: string): Promise<any | null> => {
    try {
      const q = query(
        clientsCol(ownerId),
        where('studentUid', '==', studentUid),
        limit(1)
      );
      const snapshot = await getDocs(q);
      if (snapshot.empty) return null;
      const doc = snapshot.docs[0];
      const data = doc.data() as any;
      return {
        id: doc.id,
        ownerId,
        studentUid: data.studentUid || null,
        primaryClientId: doc.id,
        clientIds: [],
        profile: {
          name: data.name || '',
          phone: data.phone || '',
          email: data.email || '',
          birthdate: data.birthdate || '',
        },
        authorizedCourses: data.academy?.authorizedCourses || [],
        progress: data.academy?.progress || {},
        audit: data.audit,
      };
    } catch (error) {
      console.error(`[AcademyService:getStudentByUid] ❌ Error:`, error);
      return null;
    }
  },

  updateStudentCourses: async (
    ownerId: string,
    studentUid: string | null,
    courses: string[],
    clientId?: string
  ): Promise<void> => {
    // Find the client doc
    let studentDocRef: any;
    let resolvedClientId: string;
    let resolvedStudentUid: string | null;
    let clientName = '';

    if (studentUid) {
      const studentQuery = query(clientsCol(ownerId), where('studentUid', '==', studentUid), limit(1));
      const studentSnap = await getDocs(studentQuery);
      if (studentSnap.empty) throw new Error('Student client record not found');
      studentDocRef = studentSnap.docs[0].ref;
      resolvedClientId = studentSnap.docs[0].id;
      resolvedStudentUid = studentUid;
      clientName = (studentSnap.docs[0].data() as any).name || '';
    } else if (clientId) {
      studentDocRef = clientDoc(ownerId, clientId);
      const snap = await getDoc(studentDocRef);
      if (!snap.exists()) throw new Error('Client record not found');
      resolvedClientId = clientId;
      resolvedStudentUid = (snap.data() as any).studentUid || null;
      clientName = (snap.data() as any).name || '';
    } else {
      throw new Error('Either studentUid or clientId is required');
    }

    // Update the unified client record
    await updateDoc(studentDocRef, {
      'academy.isStudent': true,
      'academy.authorizedCourses': courses,
      'academy.lastActive': serverTimestamp(),
      'audit.updatedAt': serverTimestamp(),
    });

    // Sync enrollments to match the new authorized courses
    const activeEnrollmentsSnap = await getDocs(query(
      enrollmentsCol(ownerId),
      where('clientId', '==', resolvedClientId),
      where('status', '==', 'active')
    ));

    const enrollmentCourseIds = new Set<string>();
    await Promise.all(activeEnrollmentsSnap.docs.map(async (enrollmentDoc) => {
      const enrollment = enrollmentDoc.data() as Enrollment;
      enrollmentCourseIds.add(enrollment.courseId);

      if (!courses.includes(enrollment.courseId)) {
        // 📉 Decrement Enrollment Count
        await updateDoc(courseRef(ownerId, enrollment.courseId), {
            enrollmentCount: increment(-1)
        }).catch(() => null);

        await updateDoc(enrollmentDoc.ref, {
          status: 'revoked',
          'audit.updatedAt': serverTimestamp(),
        });
      }
    }));

    await Promise.all(courses
      .filter((courseId) => !enrollmentCourseIds.has(courseId))
      .map(async (courseId) => {
        // 📈 Increment Enrollment Count
        await updateDoc(courseRef(ownerId, courseId), {
            enrollmentCount: increment(1)
        }).catch(() => null);

        return addDoc(enrollmentsCol(ownerId), {
          businessId: ownerId,
          clientId: resolvedClientId,
          studentUid: resolvedStudentUid,
          courseId,
          status: 'active',
          activationToken: null,
          studentProfile: {
            name: clientName,
          },
          completedLessons: [],
          audit: {
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            createdBy: ownerId,
          },
        } as any);
      }));
  }
};
